/* eslint-disable react/require-default-props */
import React, { useState } from "react";
import { useRouter } from "next/router";

import { getUrlPath } from "utils/helpers";
import JobCardsList from "components/ThemedComponent/Job/components/JobCardList";
import { JobFeedContext } from "context/Job/JobFeedContext";
import { USER_PLATFORM } from "constants/tracking";
import dynamic from "next/dynamic";
import { JobArea, JobCategory, JobCity } from "types/job";

import { JobCompany } from "types/job/JobCompany";
import JobListCard from "components/Job/JobListCard";
import { ThemeProvider } from "styled-components";
import { theme } from "theme";
import useJobFeedStore from "components/RevampFeedDetails/JobFeed/store";
import Type5Module from "components/RevampFeedDetails/JobFeed/components/Modules/Type5Module";
import { categoryToTypeMapping } from "utils/jobFeedApiParser";
import Type12Module from "components/RevampFeedDetails/JobFeed/components/Modules/Type12Module";
import { DesktopContainer } from "./styles";
import { JobFeedLoadingSkeleton } from "../JobFeedLoadingSkeleton";

const JobListPagination = dynamic(
	() => import("components/ThemedComponent/Job/JobListPagination")
);

const DownloadAppModal = dynamic(() => import("../DownloadAppModal"));

const NoJobsView = dynamic(() => import("../NoJobsView"), {
	ssr: false,
});

type JobFeedPropsType = {
	pageName: string;
	slug: string;
	selectedState?: { id: number; name: string; slug: string };
	filterStates: {
		category: JobCategory[];
		city: JobCity[];
		area: JobArea[];
		company: JobCompany[];
		educationLevel: { level: number }[];
		gender: { slug: string };
	};
	pageNumber: number;
	availableVendor?: string;
	filterCount: number;
	search: boolean;
	onPageinationClick: () => void;
	isJobFeedPage: boolean;
	isCandidateExperienced: boolean;
};

const JobFeed: React.FC<JobFeedPropsType> = ({
	pageName,
	slug,
	selectedState,
	filterStates,
	pageNumber,
	availableVendor,
	filterCount,
	search,
	onPageinationClick,
	isJobFeedPage,
	isCandidateExperienced,
}) => {
	const { jobsInStore, jobsCount, totalJobsPages, areJobsLoading, applyFilter } =
		useJobFeedStore();
	const [showDownloadAppModal, setShowDownloadAppModal] = useState(false);
	const router = useRouter();

	const commonTrackingObj = {
		Source: pageName,
		city: filterStates?.city[0]?.name || "All",
		area: filterStates?.area[0]?.name || "All",
		category: filterStates?.category[0]?.type || "All",
		state: selectedState?.name || "All",
		// company: filterStates?.company[0]?.name || "All",
		isLoggedIn: false,
		selectedJobFeedType: null,
		userPlatform: USER_PLATFORM.apna,
		page: getUrlPath(),
	};

	const isInternationalPage = router.pathname === "/international/jobs";
	if (areJobsLoading) {
		return (
			<div className="flex w-full items-center justify-center">
				<JobFeedLoadingSkeleton />
			</div>
		);
	}
	return (
		<JobFeedContext.Provider
			// eslint-disable-next-line react/jsx-no-constructed-context-values
			value={{
				...commonTrackingObj,
			}}
		>
			<div>
				{!jobsInStore?.length && <NoJobsView filterCount={filterCount} />}
				{jobsInStore?.length > 0 && (
					<div className="mt-[12px] md:mt-0">
						<JobCardsList isInternationalPage={isInternationalPage}>
							{jobsInStore?.map((job, index) => {
								if (job?.type === categoryToTypeMapping.job_card_square) {
									return (
										<div
											data-testid="job-card"
											className="mx-[12px] md:m-0"
											key={job?.data?.id}
										>
											<JobListCard
												job={job?.data}
												source={pageName}
												filterStates={filterStates}
												row={Math.floor(index / 2)}
												column={index % 2}
												pageNumber={pageNumber}
												availableVendor={availableVendor}
												filterCount={filterCount}
												search={search}
												isJobFeedPage={isJobFeedPage}
											/>
										</div>
									);
								}

								if (
									job?.type ===
									categoryToTypeMapping.job_collection_section_compact
								) {
									return <Type12Module data={job?.data} id={job?.id} />;
								}
								if (job?.type === categoryToTypeMapping.job_collection) {
									return (
										<Type5Module
											data={job?.data}
											id={job?.id}
											isCandidateExperienced={
												isCandidateExperienced
											}
										/>
									);
								}
								return null;
							})}
						</JobCardsList>
						{jobsCount > 15 && totalJobsPages > 1 && (
							<div className="flex items-center justify-center">
								<ThemeProvider theme={theme.apna}>
									<JobListPagination
										handlePageChange={(e, pageNo) => {
											applyFilter("page", pageNo);
											onPageinationClick();
										}}
										totalPages={totalJobsPages}
										currentPage={pageNumber}
										baseUrl={`/jobs/${slug}`}
										isSSRPagination={false}
									/>
								</ThemeProvider>
							</div>
						)}
					</div>
				)}

				{showDownloadAppModal && (
					<DesktopContainer>
						<DownloadAppModal
							showOverlay={showDownloadAppModal}
							onClose={() => {
								setShowDownloadAppModal(false);
							}}
						/>
					</DesktopContainer>
				)}
			</div>
		</JobFeedContext.Provider>
	);
};

JobFeed.defaultProps = {
	selectedState: { id: -1, slug: "all", name: "All" },
};

export default JobFeed;
